import { useEffect, useState } from "react";
import { Wallet, Package2, ListChecks, Gift, Banknote, Clock, CheckCircle2, AlertTriangle, Pause } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { formatCHF, formatDateTime, STATUS_LABEL } from "@/lib/format";

const PAYOUT_STATUS = {
  external_remuneration: { label: "Fuori Stripe · da verificare", icon: Clock, cls: "bg-slate-50 text-slate-700" },
  ready: { label: "Registrati, da riconciliare", icon: Clock, cls: "bg-blue-50 text-blue-700" },
  scheduled: { label: "In esecuzione", icon: Clock, cls: "bg-violet-50 text-violet-700" },
  transferred: { label: "Trasferiti", icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-700" },
  failed: { label: "Falliti", icon: AlertTriangle, cls: "bg-red-50 text-red-700" },
  blocked: { label: "Bloccati", icon: Pause, cls: "bg-orange-50 text-orange-700" },
  under_review: { label: "In revisione", icon: Pause, cls: "bg-amber-50 text-amber-700" },
  pending_connect: { label: "Stato storico Connect", icon: AlertTriangle, cls: "bg-slate-50 text-slate-700" },
  pending_payment: { label: "In attesa pagamento cliente", icon: Clock, cls: "bg-slate-50 text-slate-700" },
  cancelled: { label: "Annullati", icon: AlertTriangle, cls: "bg-slate-50 text-slate-500" },
};

export default function DriverEarnings() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [payouts, setPayouts] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/driver/stats").then(({ data }) => data),
      api.get("/driver/payouts/summary").then(({ data }) => data),
    ])
      .then(([s, p]) => { setStats(s); setPayouts(p); })
      .finally(() => setLoading(false));
  }, []);

  if (loading || !stats || !payouts) return <div className="card-soft p-10 text-center text-dapnow-muted">Caricamento…</div>;

  const bonus = user?.earnings_bonus || 0;
  const total = (stats.earnings || 0) + bonus;
  const t = payouts.totals || {};

  return (
    <div className="space-y-6" data-testid="driver-earnings-page">
      <div>
        <p className="overline">I tuoi numeri</p>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tighter mt-1">Guadagni</h1>
        <p className="text-sm text-dapnow-muted mt-2">Consulta le consegne e il riepilogo storico dei compensi.</p>
      </div>

      <div className="card-soft p-5 text-sm text-dapnow-muted" data-testid="internal-remuneration-note">
        La remunerazione dei driver interni è gestita da DapNow fuori da Stripe. Gli importi registrati sono calcoli da verificare con il tuo referente: non definiscono il compenso concordato e non attestano un pagamento. Gli stati storici sono conservati.
      </div>

      <section className="grid sm:grid-cols-4 gap-4">
        <div className="card-soft p-6 sm:col-span-2">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700"><Wallet className="h-5 w-5" /></span>
          <p className="overline mt-4">Importo registrato</p>
          <p className="font-display text-4xl font-extrabold tracking-tighter mt-1">{formatCHF(total)}</p>
          <p className="text-xs text-dapnow-muted mt-1">
            Consegne {formatCHF(stats.earnings)} {bonus > 0 && <>+ Bonus referral {formatCHF(bonus)}</>}
          </p>
        </div>
        <div className="card-soft p-6">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-dapnow-blue-soft text-dapnow-blue"><Package2 className="h-5 w-5" /></span>
          <p className="overline mt-4">Completate</p>
          <p className="font-display text-3xl font-extrabold tracking-tight mt-1">{stats.delivered_count}</p>
        </div>
        <div className="card-soft p-6">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-violet-50 text-violet-700"><ListChecks className="h-5 w-5" /></span>
          <p className="overline mt-4">Attive</p>
          <p className="font-display text-3xl font-extrabold tracking-tight mt-1">{stats.active_count}</p>
        </div>
      </section>

      {/* Payouts breakdown */}
      <section data-testid="payouts-breakdown" className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <PayoutTile statusKey="ready" amount={t.ready} testid="payout-tile-ready" />
        <PayoutTile statusKey="transferred" amount={t.transferred} testid="payout-tile-transferred" />
        {payouts.payouts_authorized === true
          ? <PayoutTile statusKey="pending_connect" amount={t.pending_connect} testid="payout-tile-pending-connect" />
          : <PayoutTile statusKey="external_remuneration" amount={t.external_remuneration} testid="payout-tile-external" />}
        <PayoutTile statusKey="failed" amount={t.failed} testid="payout-tile-failed" />
      </section>

      {/* Recent payouts */}
      <section className="card-soft p-6">
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-display text-lg font-bold tracking-tight flex items-center gap-2">
            <Banknote className="h-5 w-5 text-dapnow-blue" /> Registrazioni recenti
          </h2>
          <span className="text-xs text-dapnow-muted">Pagamento gestito da DapNow</span>
        </div>
        <div className="mt-4 divide-y divide-dapnow-line">
          {(payouts.recent || []).length === 0 && (
            <p className="text-sm text-dapnow-muted py-4">Nessuna registrazione ancora.</p>
          )}
          {(payouts.recent || []).map((p) => {
            const meta = PAYOUT_STATUS[p.status] || { label: p.status, cls: "bg-slate-50 text-slate-700", icon: Clock };
            const Icon = meta.icon;
            return (
              <div key={p.id} className="py-3 flex items-center justify-between gap-3" data-testid={`payout-row-${p.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold truncate">Ordine #{p.order_id?.slice(0, 8)}</p>
                  <p className="text-xs text-dapnow-muted">{formatDateTime(p.created_at)} · {p.delivery_type}</p>
                </div>
                <div className="text-right">
                  <p className="font-display text-base font-bold">{formatCHF(p.driver_payout_amount)}</p>
                  <span className={`inline-flex items-center gap-1 mt-0.5 pill text-[11px] ${meta.cls}`}>
                    <Icon className="h-3 w-3" /> {meta.label}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {bonus > 0 && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 flex items-center gap-3 text-sm text-emerald-900">
          <Gift className="h-5 w-5" />
          <p>Hai accumulato <strong>{formatCHF(bonus)}</strong> di bonus referral. Continua a invitare driver per guadagnare ancora.</p>
        </div>
      )}

      <section className="card-soft p-6">
        <h2 className="font-display text-lg font-bold tracking-tight">Per stato consegne</h2>
        <div className="mt-4 space-y-2.5">
          {Object.entries(stats.by_status).map(([s, c]) => (
            <div key={s} className="flex items-center justify-between text-sm">
              <span>{STATUS_LABEL[s] || s}</span>
              <span className="font-display text-base font-semibold">{c}</span>
            </div>
          ))}
          {Object.keys(stats.by_status).length === 0 && <p className="text-sm text-dapnow-muted">Ancora nessuna attività.</p>}
        </div>
      </section>
    </div>
  );
}

function PayoutTile({ statusKey, amount, testid }) {
  const meta = PAYOUT_STATUS[statusKey] || { label: statusKey, cls: "bg-slate-50 text-slate-700", icon: Clock };
  const Icon = meta.icon;
  return (
    <div className="card-soft p-5" data-testid={testid}>
      <span className={`inline-flex h-9 w-9 items-center justify-center rounded-xl ${meta.cls}`}>
        <Icon className="h-4 w-4" />
      </span>
      <p className="overline mt-3 text-[10px]">{meta.label}</p>
      <p className="font-display text-2xl font-extrabold tracking-tight mt-1">{formatCHF(amount || 0)}</p>
    </div>
  );
}
